import React, { useState, useEffect, useRef, useMemo} from 'react';
import MentionAutocomplete, { buildRefLabel } from '../genbi/MentionAutocomplete';

const ImageComponent = ({ imageData, isLoading, contentId, subtitle, footer, isEditing = false, onFieldChange, hideContentLabel = false,
  docs = [], refs = [], onNeedReferences, onCreateReference, refsLoading = false })  => {
  const containerRef = useRef(null);
  const subtitleRef = useRef(null);
  const footerRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  const [zoomLevel, setZoomLevel] = useState(window.devicePixelRatio || 1);

  const processDocTags = (text) => {
    if (!text) return text;
    const maxLen = 15;
    // A raw token comes from an unsaved edit (Save Changes); build the same pill the
    // backend builds on load, with the document / reference name.
    const chip = (cls, token, label) =>
      `<span class="${cls} m-0 mx-1 ev-btn-xsmall ev-bold-reg" data-ref="${token}">${label}</span>`;
    let processed = text
      .replace(/(<[^>]*>)|\[doc_id_(\d+)\]/g, (match, tag, id) => {
        if (tag) return tag;
        const doc = docs.find(d => Number(d.id) === Number(id));
        return chip('ev-message-card6', match, doc?.name || `Document ${id}`);
      })
      .replace(/(<[^>]*>)|\[ar_id_(\d+)\]/g, (match, tag, id) => {
        if (tag) return tag;
        const ref = refs.find(r => Number(r.ar_id) === Number(id));
        return chip('ev-message-card7', match, ref ? buildRefLabel(ref) : `Reference ${id}`);
      });
    processed = processed.replace(
      /<span([^>]*\b(?:ev-message-card6|ev-message-card7)\b[^>]*)>([\s\S]*?)<\/span>/g,
      (full, attrs, inner) => {
        const rawText = (inner || '').trim();
        if (rawText.includes('<')) return full;
        const shortText = rawText.length <= maxLen ? rawText : rawText.slice(0, maxLen - 1) + '…';
        const hasTitle = /\stitle=/.test(attrs) || /\sdata-src=/.test(attrs);
        const safeTitle = rawText.replace(/"/g, '&quot;');
        const newAttrs = hasTitle ? attrs : `${attrs} title="${safeTitle}"`;
        return `<span${newAttrs}>${shortText}</span>`;
      }
    );
    // Convert JSX className to HTML class for dangerouslySetInnerHTML
    processed = processed.replace(/\bclassName=/g, 'class=');
    return processed;
  };

  const processedSubtitle = useMemo(() => processDocTags(subtitle), [subtitle, docs, refs]);
  const processedFooter = useMemo(() => processDocTags(footer), [footer, docs, refs]);
  const getCSSVariable = (variable) => {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  };
 
  // Get font size from CSS class
  const getFontSize = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontSize = parseFloat(getComputedStyle(tempElement).fontSize);
    document.body.removeChild(tempElement);
    return fontSize;
  };
 
  const getMargin = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const margin = parseFloat(getComputedStyle(tempElement).marginLeft);
    document.body.removeChild(tempElement);
    return margin;
  };
 
  // Get font weight from CSS class
  const getFontWeight = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontWeight = getComputedStyle(tempElement).fontWeight;
    document.body.removeChild(tempElement);
    return fontWeight;
  };
 
  // Get font family from CSS class
  const getFontFamily = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontFamily = getComputedStyle(tempElement).fontFamily;
    document.body.removeChild(tempElement);
    return fontFamily;
  };
 
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme');
          setIsDarkMode(newTheme === 'dark');
        }
      });
    });
 
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
 
    return () => observer.disconnect();
  }, []);
 
  useEffect(() => {
    const handleResize = () => {
      setZoomLevel(window.devicePixelRatio || 1);
    };
 
    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);
 
  if (isLoading) {
    return (
      <div className="graph-container align-items-center justify-content-center d-flex ">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <br />
          <span className="ev-reg-p-dark">
            Loading Image
          </span>
        </div>
      </div>
    );
  }
 
  if (!imageData || (!imageData.presigned_url && !imageData.bytes_base64)) {
    return (
      <div className="container-error">
        <div className="text-center">
          <p>Error loading image</p>
        </div>
      </div>
    );
  }
 
  const src = imageData.presigned_url? imageData.presigned_url : `data:${imageData.content_type || 'image/png'};base64,${imageData.bytes_base64}`;
 
  // Calculate theme colors
  const themeColors = {
    paper_bgcolor: getCSSVariable('--background2'),
    plot_bgcolor: getCSSVariable('--background2'),
    fontColor: getCSSVariable('--color4'),  
    gridColor: getCSSVariable('--color12'),
    titleColor: getCSSVariable('--color2'),
  };
 
  // Get font sizes from CSS classes
  const fontSize = getFontSize('ev-fs-11');
  const mainTitleSize = getFontSize('ev-fs-13');
 
  // Get font weights from CSS classes
  const mainTitleWeight = getFontWeight('ev-bold-med');
 
  // Get font family from CSS class
  const fontFamily = getFontFamily('poppinsfont');
 
  return (
    <div className="graph-container">
      {(isEditing || subtitle) && (
        <div className="mb-2">
          {isEditing ? (
            <div className="genbi-figure-edit-field">
              <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                <i className="bi bi-image"></i> <b>Figure {contentId} — Subtitle</b>
              </span>
              <textarea
                ref={subtitleRef}
                className="formField2 ev-fs-11 w-100 genbi-footer-textarea"
                value={subtitle || ''}
                onChange={(e) => onFieldChange?.('subtitle', e.target.value)}
                rows={2}
                placeholder="Edit subtitle..."
              />
            </div>
          ) : (
            <span className="ev-message-card2 w-100 m-0 ev-fs-11 text-wrap">
              <i className="bi bi-image"></i> <b>Figure {contentId}. </b> <span dangerouslySetInnerHTML={{ __html: processedSubtitle }} />
            </span>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        className="image-display-wrapper"
        style={{
          backgroundColor: themeColors.paper_bgcolor,
          borderColor: themeColors.gridColor,
        }}
        key={`${isDarkMode ? 'dark' : 'light'}-${zoomLevel}`}
      >
        <img
          src={src}
          alt={subtitle || `Figure ${contentId}`}
        />
      </div>
            {(isEditing || footer) && (
        <div className="mb-2">
          {isEditing ? (
            <div className="genbi-figure-edit-field">
              <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                <i className="bi bi-pencil-sticky"></i> <b>Note</b>
              </span>
              <textarea
                ref={footerRef}
                className="formField2 ev-fs-11 w-100 genbi-footer-textarea"
                value={footer || ''}
                onChange={(e) => onFieldChange?.('footer', e.target.value)}
                rows={2}
                placeholder="Edit footnote..."
              />
            </div>
          ) : (
            <span className="ev-message-card2 w-100 m-0 ev-fs-11 text-wrap">
              <i className="bi bi-pencil-sticky"></i>  <b>Note </b><span dangerouslySetInnerHTML={{ __html: processedFooter }} />
            </span>
          )}
        </div>
      )}
      {isEditing && [['subtitle', subtitleRef], ['footer', footerRef]].map(([field, ref]) => (
        <MentionAutocomplete
          key={field}
          textareaRef={ref}
          docs={docs}
          refs={refs}
          onNeedReferences={onNeedReferences}
          onCreateReference={onCreateReference}
          refsLoading={refsLoading}
          onInsert={(newValue, newCaret) => {
            // Subtitle/note are plain text: swap the chip HTML for its [doc_id_N]/[ar_id_N]
            // token, which processDocTags turns back into a badge.
            const toToken = (s) => s.replace(/<span[^>]*data-ref="([^"]+)"[^>]*>[^<]*<\/span>/g, '$1');
            const value = toToken(newValue);
            const caret = toToken(newValue.slice(0, newCaret)).length;
            onFieldChange?.(field, value);
            requestAnimationFrame(() => {
              const ta = ref.current;
              if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); }
            });
          }}
        />
      ))}
    </div>
  );
};

export default ImageComponent;